import { useState, useCallback, useEffect } from 'react';
import { FileIcon, SpinnerIcon, ChevronIcon } from './icons';

import FileUploadMode from './documents/FileUploadMode';
import UrlIngestMode from './documents/UrlIngestMode';
import DocumentList from './documents/DocumentList';
import IngestReportModal from './documents/IngestReportModal';

const ALLOWED_EXT = ['.pdf', '.docx', '.doc', '.html', '.htm', '.txt', '.md', '.pptx', '.xlsx'];

export default function DocumentsPanel({ sessionId, documents, onUploadMany, onIngestUrl, onDelete, onReassess }) {
  const [expanded, setExpanded]               = useState(true);
  const [mode, setMode]                       = useState('file'); // 'file' | 'url'
  const [uploading, setUploading]             = useState(false);
  const [uploadStatus, setUploadStatus]       = useState(null);
  const [uploadErrors, setUploadErrors]       = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [copiedId, setCopiedId]               = useState(null);
  const [urlInput, setUrlInput]               = useState('');
  const [urlLoading, setUrlLoading]           = useState(false);
  const [urlError, setUrlError]               = useState(null);
  const [reportDoc, setReportDoc]             = useState(null);
  const [qualityFilter, setQualityFilter] = useState('all');

  const pendingCount = documents.filter(d => d.status === 'pending').length;
  const busy = uploading || urlLoading;

  const handleModeSwitch = (next) => {
    setMode(next);
    setUploadErrors([]);
    setUrlError(null);
  };

  const handleFilesSelected = useCallback(async (rawFiles) => {
    const invalid = rawFiles.filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return !ALLOWED_EXT.includes(ext);
    });
    if (invalid.length) {
      setUploadErrors(invalid.map(f => ({
        filename: f.name,
        error: `Unsupported file type ".${f.name.split('.').pop()}"`,
      })));
      return;
    }

    setUploadErrors([]);
    setUploading(true);
    setUploadStatus(null);

    const errors = await onUploadMany(rawFiles, ({ filename, loaded, total, fileIndex, fileCount }) => {
      setUploadStatus({
        filename,
        fileIndex: fileIndex + 1,
        fileCount,
        pct: total ? Math.round((loaded / total) * 100) : 0,
      });
    });

    setUploading(false);
    setUploadStatus(null);
    if (errors.length) {
      setUploadErrors(errors.map(e => ({
        filename: e.filename,
        error: e.error?.response?.data?.detail || 'Upload failed.',
      })));
    }
  }, [onUploadMany]);

  const handleUrlIngest = useCallback(async () => {
    const url = urlInput.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setUrlError('URL must start with http:// or https://');
      return;
    }
    setUrlError(null);
    setUrlLoading(true);
    try {
      await onIngestUrl(url);
      setUrlInput('');
    } catch (err) {
      setUrlError(err?.response?.data?.detail || 'Failed to ingest URL.');
    } finally {
      setUrlLoading(false);
    }
  }, [urlInput, onIngestUrl]);

  const handleDeleteClick   = (docId) => setConfirmDeleteId(docId);
  const handleDeleteConfirm = async () => {
    if (confirmDeleteId) await onDelete(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const handleCopyFilename = async (doc) => {
    try {
      await navigator.clipboard.writeText(doc.filename);
    } catch {
      const el = document.createElement('textarea');
      el.value = doc.filename;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopiedId(doc.id);
    setTimeout(() => setCopiedId(id => id === doc.id ? null : id), 2000);
  };

  const confirmTarget = documents.find(d => d.id === confirmDeleteId);

  const warnCount = documents.filter(d => d.quality_verdict === 'warn').length;
  const poorCount = documents.filter(d => d.quality_verdict === 'poor').length;
  const showFilter = warnCount + poorCount > 0;

  const filteredDocuments = qualityFilter === 'all'
    ? documents
    : documents.filter(d => d.quality_verdict === qualityFilter);

  useEffect(() => { setQualityFilter('all'); }, [sessionId]);

  const handleReassess = async (docId) => {
    const result = await onReassess(docId);
    if (result) {
      setReportDoc(prev =>
        prev?.id === docId
          ? { ...prev, quality_verdict: result.verdict, ingest_report: result.report }
          : prev
      );
    }
  };
  
  return (
    <div className="sidebar-prompts-panel">

      {/* Header */}
      <button
        className="sidebar-prompts-header"
        onClick={() => setExpanded(v => !v)}
        aria-expanded={expanded}
      >
        <FileIcon />
        <span>Documents</span>
        {documents.length > 0 && (
          <span className="sidebar-prompts-count">{documents.length}</span>
        )}
        {pendingCount > 0 && (
          <span style={{ marginLeft: 'auto', marginRight: '4px', display: 'flex', alignItems: 'center' }}>
            <SpinnerIcon />
          </span>
        )}
        <ChevronIcon up={expanded} />
      </button>

      {expanded && (
        <div className="sidebar-prompts-body">

          {/* Pill toggle — File / URL */}
          <div className="doc-pill-wrap">
            <div className="doc-pill-track">
              {['file', 'url'].map(m => (
                <button
                  key={m}
                  className={`doc-pill-btn ${mode === m ? 'doc-pill-btn--active' : ''}`}
                  onClick={() => handleModeSwitch(m)}
                  disabled={busy}
                >
                  {m === 'file' ? 'File' : 'URL'}
                </button>
              ))}
            </div>
          </div>

          {mode === 'file' && (
            <FileUploadMode
              uploading={uploading}
              uploadStatus={uploadStatus}
              uploadErrors={uploadErrors}
              onFilesSelected={handleFilesSelected}
            />
          )}

          {mode === 'url' && (
            <UrlIngestMode
              urlInput={urlInput}
              setUrlInput={setUrlInput}
              urlError={urlError}
              setUrlError={setUrlError}
              urlLoading={urlLoading}
              onSubmit={handleUrlIngest}
            />
          )}

          {/* Inline delete confirmation */}
          {confirmDeleteId && confirmTarget && (
            <div className="documents-confirm">
              <span>Delete <strong>{confirmTarget.filename}</strong>?</span>
              <div className="documents-confirm-actions">
                <button className="documents-confirm-yes" onClick={handleDeleteConfirm}>Delete</button>
                <button className="documents-confirm-no"  onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              </div>
            </div>
          )}

          {showFilter && (
            <div className="doc-quality-filter">
              {[
                { key: 'all',  label: `all (${documents.length})` },
                { key: 'warn', label: `⚠ warn (${warnCount})`,  hide: warnCount === 0 },
                { key: 'poor', label: `✗ poor (${poorCount})`,  hide: poorCount === 0 },
              ].filter(t => !t.hide).map(({ key, label }) => (
                <button
                  key={key}
                  className={`doc-quality-btn ${qualityFilter === key ? 'doc-quality-btn--active' : ''}`}
                  onClick={() => setQualityFilter(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <DocumentList
            documents={filteredDocuments}
            confirmDeleteId={confirmDeleteId}
            copiedId={copiedId}
            onDeleteClick={handleDeleteClick}
            onCopyFilename={handleCopyFilename}
            onViewReport={setReportDoc}
          />

          {documents.length > 0 && (
            <p className="documents-hint">
              {pendingCount > 0
                ? `Indexing ${pendingCount} document${pendingCount !== 1 ? 's' : ''}… Ask the AI once ready.`
                : 'All documents indexed — ask the AI about their contents.'}
            </p>
          )}

        </div>
      )}
      <IngestReportModal
        doc={reportDoc}
        onClose={() => setReportDoc(null)}
        onReassess={handleReassess}
      />
    </div>
  );
}